import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../../../api/client';
import StudentForm from './StudentForm';
import styles from './StudentsPage.module.css';

export default function StudentsPage() {
  const navigate = useNavigate();
  const [students, setStudents] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [editStudent, setEditStudent] = useState<any>(null);

  const fetchStudents = async () => {
    setLoading(true);
    try {
      const data = await api.get<any[]>('/api/students');
      setStudents(Array.isArray(data) ? data : []);
    } catch {
      /* ignore */
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchStudents();
  }, []);

  const handleDelete = async (id: string) => {
    if (
      !confirm(
        '¿Eliminar este alumno? También se eliminarán sus registros de asistencia, pagos y calificaciones.',
      )
    )
      return;
    try {
      await api.delete(`/api/students/${id}`);
      fetchStudents();
    } catch {
      alert('Error al eliminar');
    }
  };

  const openEdit = (student: any) => {
    setEditStudent(student);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditStudent(null);
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h1>Gestión de Alumnos</h1>
        <div className={styles.headerButtons}>
          <button className={styles.bulkButton} onClick={() => setShowBulkModal(true)}>
            📤 Carga Masiva
          </button>
          <button className={styles.addButton} onClick={() => setShowModal(true)}>
            + Nuevo Alumno
          </button>
        </div>
      </div>

      <div className={styles.statsGrid}>
        <div className={styles.statCard}>
          <h3>Total Alumnos</h3>
          <p>{students.length}</p>
        </div>
      </div>

      <div className={styles.tableContainer}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th>Nombre Completo</th>
              <th>CURP</th>
              <th>Grado/Grupo</th>
              <th>Estatus</th>
              <th>Acciones</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={5} style={{ textAlign: 'center' }}>
                  Cargando...
                </td>
              </tr>
            ) : students.length === 0 ? (
              <tr>
                <td colSpan={5} style={{ textAlign: 'center' }}>
                  No hay alumnos registrados
                </td>
              </tr>
            ) : (
              students.map((student) => (
                <tr key={student.id}>
                  <td>
                    {student.paternalLastName}{student.maternalLastName ? ` ${student.maternalLastName}` : ''}, {student.firstName}
                  </td>
                  <td>{student.curp || 'N/A'}</td>
                  <td>
                    {student.enrollments?.[0]
                      ? `${student.enrollments[0].group?.grade?.name} - ${student.enrollments[0].group?.name}`
                      : 'Sin asignar'}
                  </td>
                  <td>
                    <span className={styles.statusBadge}>
                      {student.enrollments?.[0]?.status || 'Inactivo'}
                    </span>
                  </td>
                  <td>
                    <div className={styles.actionGroup}>
                      <button
                        className={styles.actionBtn}
                        onClick={() => navigate(`/dashboard/students/${student.id}`)}
                      >
                        Ver
                      </button>
                      <button className={styles.editBtn} onClick={() => openEdit(student)}>
                        Editar
                      </button>
                      <button className={styles.deleteBtn} onClick={() => handleDelete(student.id)}>
                        Eliminar
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {showModal && (
        <StudentForm
          onClose={closeModal}
          onSuccess={fetchStudents}
          student={editStudent || undefined}
        />
      )}

      {showBulkModal && (
        <BulkUploadModal
          onClose={() => setShowBulkModal(false)}
          onSuccess={() => {
            setShowBulkModal(false);
            fetchStudents();
          }}
        />
      )}
    </div>
  );
}

function BulkUploadModal({ onClose, onSuccess }: { onClose: () => void; onSuccess: () => void }) {
  const [file, setFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState<any>(null);
  const [error, setError] = useState('');

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      setFile(selectedFile);
      setError('');
      setResult(null);
    }
  };

  const handleUpload = async () => {
    if (!file) {
      setError('Por favor selecciona un archivo');
      return;
    }

    setUploading(true);
    setError('');

    try {
      const text = await file.text();
      const lines = text.split('\n').filter((line) => line.trim());
      
      if (lines.length < 2) {
        throw new Error('El archivo debe tener al menos una fila de datos');
      }

      const headers = lines[0].split(',').map((h) => h.trim().toLowerCase());
      const students = [];

      for (let i = 1; i < lines.length; i++) {
        const values = lines[i].split(',').map((v) => v.trim());
        const student: any = {};

        headers.forEach((header, index) => {
          const value = values[index] || '';
          if (header.includes('nombre') && !header.includes('padre') && !header.includes('madre')) {
            student.firstName = value;
          } else if (header.includes('apellido paterno') && !header.includes('padre') && !header.includes('madre')) {
            student.paternalLastName = value;
          } else if (header.includes('apellido materno') && !header.includes('padre') && !header.includes('madre')) {
            student.maternalLastName = value;
          } else if (header.includes('curp')) {
            student.curp = value;
          } else if (header.includes('fecha') || header.includes('birth')) {
            student.birthDate = value;
          } else if (header.includes('sangre') || header.includes('blood')) {
            student.bloodType = value;
          } else if (header.includes('alergia') || header.includes('allerg')) {
            student.allergies = value;
          } else if (header.includes('emergencia') || header.includes('emergency')) {
            student.emergencyContact = value;
          } else if (header.includes('nivel') || header.includes('level')) {
            if (value.toLowerCase().includes('prim')) {
              student.level = 'PRIMARY';
            } else if (value.toLowerCase().includes('secun')) {
              student.level = 'SECONDARY';
            } else if (value.toLowerCase().includes('prepa') || value.toLowerCase().includes('bachiller')) {
              student.level = 'HIGH_SCHOOL';
            } else {
              student.level = 'PRIMARY';
            }
          } else if (header.includes('padre') && header.includes('nombre')) {
            student.fatherFirstName = value;
          } else if (header.includes('padre') && header.includes('apellido paterno')) {
            student.fatherPaternalLastName = value;
          } else if (header.includes('padre') && header.includes('apellido materno')) {
            student.fatherMaternalLastName = value;
          } else if (header.includes('padre') && header.includes('email')) {
            student.fatherEmail = value;
          } else if (header.includes('madre') && header.includes('nombre')) {
            student.motherFirstName = value;
          } else if (header.includes('madre') && header.includes('apellido paterno')) {
            student.motherPaternalLastName = value;
          } else if (header.includes('madre') && header.includes('apellido materno')) {
            student.motherMaternalLastName = value;
          } else if (header.includes('madre') && header.includes('email')) {
            student.motherEmail = value;
          }
        });

        if (student.firstName && student.paternalLastName) {
          students.push(student);
        }
      }

      const response = await api.post('/api/students/bulk', { students });
      setResult(response);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className={styles.modalOverlay} onClick={onClose}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div className={styles.modalHeader}>
          <h2>Carga Masiva de Alumnos</h2>
          <button className={styles.modalClose} onClick={onClose}>
            ✕
          </button>
        </div>

        <div className={styles.modalBody}>
          <div className={styles.instructions}>
            <h3>Instrucciones:</h3>
            <p>Sube un archivo CSV con las siguientes columnas:</p>
            <ul>
              <li><strong>Nombre</strong> (obligatorio)</li>
              <li><strong>Apellido Paterno</strong> (obligatorio)</li>
              <li><strong>Apellido Materno</strong> (opcional)</li>
              <li><strong>CURP</strong> (opcional, 18 caracteres)</li>
              <li><strong>Fecha de Nacimiento</strong> (opcional, formato YYYY-MM-DD)</li>
              <li><strong>Tipo de Sangre</strong> (opcional)</li>
              <li><strong>Alergias</strong> (opcional)</li>
              <li><strong>Contacto de Emergencia</strong> (opcional)</li>
              <li><strong>Nivel</strong> (opcional: Primaria, Secundaria, Preparatoria)</li>
              <li><strong>Nombre Padre</strong> (opcional)</li>
              <li><strong>Apellido Paterno Padre</strong> (opcional)</li>
              <li><strong>Apellido Materno Padre</strong> (opcional)</li>
              <li><strong>Email Padre</strong> (opcional)</li>
              <li><strong>Nombre Madre</strong> (opcional)</li>
              <li><strong>Apellido Paterno Madre</strong> (opcional)</li>
              <li><strong>Apellido Materno Madre</strong> (opcional)</li>
              <li><strong>Email Madre</strong> (opcional)</li>
            </ul>
            <p className={styles.note}>
              <strong>Nota:</strong> El sistema detecta duplicados automáticamente:
              <br />• Si el alumno tiene CURP y ya existe, se actualizará.
              <br />• Si no tiene CURP, se buscará por nombre completo (nombre + apellido paterno + apellido materno).
              <br />• Si se encuentra un duplicado, se actualizará; si no, se creará uno nuevo.
            </p>
          </div>

          <div className={styles.uploadSection}>
            <input
              type="file"
              accept=".csv,.txt"
              onChange={handleFileChange}
              className={styles.fileInput}
              id="file-upload"
            />
            <label htmlFor="file-upload" className={styles.fileLabel}>
              {file ? file.name : 'Seleccionar archivo'}
            </label>

            {error && <div className={styles.error}>{error}</div>}

            {result && (
              <div className={styles.success}>
                <h4>✅ Carga completada</h4>
                <p>Alumnos creados: {result.results?.created || 0}</p>
                <p>Alumnos actualizados: {result.results?.updated || 0}</p>
                <p>Alumnos omitidos: {result.results?.skipped || 0}</p>
                {result.results?.errors && result.results.errors.length > 0 && (
                  <div className={styles.errorList}>
                    <strong>Errores:</strong>
                    <ul>
                      {result.results.errors.map((err: string, i: number) => (
                        <li key={i}>{err}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}

            <div className={styles.modalFooter}>
              <button onClick={onClose} className={styles.cancelBtn}>
                {result ? 'Cerrar' : 'Cancelar'}
              </button>
              {!result && (
                <button
                  onClick={handleUpload}
                  disabled={uploading || !file}
                  className={styles.submitBtn}
                >
                  {uploading ? 'Procesando...' : 'Cargar Alumnos'}
                </button>
              )}
              {result && (
                <button onClick={onSuccess} className={styles.submitBtn}>
                  Finalizar
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
